<template>
  <div class="mb-3">
    <div class="row task-title">
      <div class="col-7 d-flex align-items-center">
        <div v-show="editMode" style="margin-left: -12px;">
          <label for="title" hidden>Task Title</label>
          <textarea
              id="title"
              class="form-control task-title-input"
              rows="1"
              ref="task-title-input"
              v-model="title"
              :style="titleInputFieldStyle"
              @keydown.esc="cancelEditing"
              @keydown.enter="saveChanges"
          />
        </div>
        <div v-show="!editMode">
          <div class="d-flex align-self-center" :class="{ 'text-muted': !title }" @click="triggerEditMode">
            <div class="mr-2">{{ title }}</div>
          </div>
        </div>
      </div>

      <div class="col-5 text-right d-flex flex-row justify-content-end align-items-center playable-controller">
        <button
            class="btn btn-link no-outline shadow-none"
            style="padding: 0 !important; margin: 0 auto 0 0"
            v-if="hasDetails"
            @click="triggerDisplayDetails"
        >
          <i class="fas" :class="dropDownIcon"></i>
        </button>
        <div class="playable-timer mr-2" v-if="taskActive">{{ elapsedTime }}</div>

        <Checkbox size="medium"
                  :type="checkboxType"
                  :isPlayIcon="displayPlayIcon"
                  :isPauseIcon="displayPauseIcon"
                  @clicked="switchTaskState"
        />
      </div>
    </div>

    <div class="row" v-if="taskLabelsText">
      <div class="col">
        <div style="display: flex;">
          <span class="text-muted text-grey task-details" v-text="taskLabelsText"></span>
        </div>
      </div>
    </div>

    <div class="row align-items-center" v-if="editMode">
      <div class="col text-right pr-0">
        <button class="border-0 bg-transparent no-outline p-0">
          <img src="/images/tag.svg" style="width: 24px; height: 24px" @click="updatingLabels = true"/>
        </button>

        <button class="border-0 bg-transparent no-outline p-0" @click="promptTaskDeletion = true">
          <img src="/images/delete.svg"/>
        </button>
      </div>
    </div>

    <div v-show="showDetails">
      <div class="row mb-2 text-muted" v-for="playable of stoppedPlayable">
        <div class="col d-flex align-items-center">
          <div>
            <img src="/images/sub_drag.svg" alt=""/>
          </div>
          <div class="ml-2 playable-details">
            {{ formatDate(playable.stopped_at) }} ({{ getDifference(playable.started_at, playable.stopped_at) }})
          </div>
        </div>
      </div>
    </div>

    <task-modal-remove
        v-if="promptTaskDeletion"
        @clickDelete="deleteTask"
        @close="promptTaskDeletion = false"
    />

    <add-label-modal
        v-if="updatingLabels"
        :required="false"
        :options="labelOptions"
        :selected="selectedLabelName"
        @selectItem="saveTaskLabel"
        @deleteItem="deleteLabel"
        @updateLabel="updateLabel"
        @closeModal="updatingLabels = false"
    ></add-label-modal>

    <transition name="fade">
      <OpenTaskDetailsModal
          v-if="viewDetails"
          :task="task"
          :actionText="task.completed_at ? 'Mark as not done' : 'Mark As Done'"
          :editable="true"
          @submit="markAsDone()"
          @close="viewDetails = !viewDetails"
      />
    </transition>
  </div>
</template>

<script>
import moment from 'moment';
import { destroy, update } from '../../../../services/taskService';
import Checkbox from '../../../../atoms/Tasks/Checkbox';
import OpenTaskDetailsModal from '../../../Misc/Modal/OpenTaskDetailsModal';
import Swal from 'sweetalert2';
import {
  setWfhSnapshotDownloaded,
  getWfhSnapshotDownloaded,
} from '../../../../repositories/UserDataPersistence';
import { detect } from 'detect-browser';

export default {
  components: { OpenTaskDetailsModal, Checkbox, setWfhSnapshotDownloaded, getWfhSnapshotDownloaded, detect },

  props: ['task', 'activeTask'],

  data () {
    return {
      playable: [],
      taskActive: this.task.playable[0]?.stopped_at === null ?? true,
      editMode: false,
      title: this.task.title,
      intervalTimer: null,
      elapsedTime: null,
      showDetails: false,
      titleInputFieldHeight: 'auto',
      promptTaskDeletion: false,
      updatingLabels: false,
      selectedLabel: (this.task.labels && this.task.labels.length > 0) ? this.task.labels[0] : null,
      viewDetails: false,
      titleFlickerIntervalTimer: null,
      originalTitle: document.title,
      alertOn: false,
      hasInactiveListener: false,
    };
  },

  computed: {
    currentUser () {
      return this.$store.state.currentUser;
    },

    requiresScreenshots () {
      return this.$store.state.currentUser.requires_screenshot;
    },

    stoppedPlayable () {
      return this.playable.filter(playable => playable.stopped_at !== null);
    },

    hasDetails () {
      return this.stoppedPlayable.length > 0;
    },

    timezone () {
      return this.$store.state.timezone;
    },

    dropDownIcon () {
      return this.showDetails ? 'fa-angle-up' : 'fa-angle-down';
    },

    titleInputFieldStyle () {
      return {
        height: this.titleInputFieldHeight,
      };
    },

    labelOptions () {
      return this.$store.state.labelOptions;
    },

    selectedLabelName () {
      return this.selectedLabel ? this.selectedLabel.name : '';
    },

    taskLabelsText () {
      if (!this.task.labels) return '';

      let taskLabels = '';

      for (const label of this.task.labels) {
        taskLabels += `${label.name}, `;
      }

      return taskLabels.substr(0, taskLabels.length - 2);
    },

    isCompleted () {
      return this.task.completed_at !== null;
    },

    checkboxType () {
      return this.taskActive ? 'danger' : 'success';
    },

    isExternalMember () {
      return this.$store.state.isExternal;
    },

    displayPauseIcon () {
      if (this.isExternalMember) {
        return !this.isCompleted && this.taskActive;
      }
      return this.taskActive;
    },

    displayPlayIcon () {
      if (this.isExternalMember) {
        return !this.isCompleted && !this.taskActive;
      }
      return !this.taskActive;
    },
  },

  watch: {
    activeTask () {
      if (this.activeTask !== null && this.activeTask !== this.task.id && this.taskActive) {
        this.taskActive = false;
        this.stopTask();
        this.triggerStopTaskActions();
      }
    },
  },

  methods: {
    async markAsDone () {
      const res = await update(this.task, { is_completed: this.task.completed_at === null });
      this.task.completed_at = res.data.data.completed_at;
    },

    async switchTaskState () {
      if (this.isExternalMember && this.isCompleted) {
        await this.markAsDone();
        return;
      }

      const isActive = !this.taskActive;

      if (isActive) {
        if (this.requiresScreenshots && !getWfhSnapshotDownloaded()) {
          Swal.fire({
            allowOutsideClick: false,
            title: 'Download Snapshot App',
            text: 'Please click on view installation guide to see how to download and install the WFHSnapshot application.',
            icon: 'info',
            confirmButtonColor: '#9696a8',
            confirmButtonText: 'View Installation Guide',
          }).then((result) => {
            if (result.isConfirmed) {
              setWfhSnapshotDownloaded(true);

              window.open(
                  route('installation.snapshot'),
                  '_blank'
              )
            }
          });

          return;
        }

        await this.startTask();
        this.triggerStartTaskActions();
      } else {
        await this.stopTask();
        this.triggerStopTaskActions();
      }
    },

    async startTask () {
      const res = await axios.post(`/tasks/${this.task.id}/playable`, {
        status: 'start',
      });
      const task = res.data.data;

      this.playable = task.playable;
      this.handleTaskTimer();
    },

    async stopTask () {
      const res = await axios.post(`/tasks/${this.task.id}/playable`, {
        status: 'stop',
      });
      const task = res.data.data;

      this.playable = task.playable;
      this.handleTaskTimer();
    },

    triggerStartTaskActions () {
      this.registerUnloadHandler();
      this.taskPlayed(this.task.id);
      this.registerInactivityListener();
      this.taskActive = true;

      if (this.requiresScreenshots) {
        this.redirectToApp();
      }
    },

    triggerStopTaskActions () {
      this.deregisterUnloadHandler();
      this.taskPaused();
      this.deregisterInactivityListener();
      this.taskActive = false;
    },

    redirectToApp () {
      window.open(route('desktop.redirect', {
        task_id: this.task.id,
      }), '_blank');
    },

    handleTaskTimer () {
      if (this.intervalTimer !== null) {
        clearInterval(this.intervalTimer);
        this.intervalTimer = null;
        this.elapsedTime = null;
        return;
      }

      this.intervalTimer = setInterval(() => {
        let timeDifferencesSecs = 0;
        let timeDifferencesMinutes = 0;

        for (const item of this.playable) {
          const stoppedAt = item.stopped_at ? moment(item.stopped_at) : moment();
          timeDifferencesSecs += stoppedAt.diff(moment(item.started_at), 'seconds');
          timeDifferencesMinutes += stoppedAt.diff(moment(item.started_at), 'minutes');
        }

        const hourValue = Math.floor(timeDifferencesMinutes / 60);
        const minuteValue = Math.floor(timeDifferencesSecs % 3600 / 60);
        const secondValue = timeDifferencesSecs % 60;
        this.elapsedTime = `${hourValue}:${this.getDigits(minuteValue)}:${this.getDigits(secondValue)}`;
      }, 1000);
    },

    handleTitleNotification () {
      if (this.titleFlickerIntervalTimer !== null) {
        clearInterval(this.titleFlickerIntervalTimer);
        this.titleFlickerIntervalTimer = null;
        document.title = this.originalTitle;
        return;
      }

      this.titleFlickerIntervalTimer = setInterval(() => {
        const prefix = '(1)';

        if (this.alertOn) {
          document.title = `${prefix} Are you still there?`;
        } else {
          document.title = `${prefix} ${this.originalTitle}`;
        }

        this.alertOn = !this.alertOn;
      }, 500);
    },

    handleOvertime () {
      const message = `<p><b>${this.task.title}</b> has been started for more than 2 hours, do you want to continue?</p>`;
      this.taskActive = false;
      this.handleTaskTimer();
      this.handleTitleNotification();

      Swal.fire({
        imageUrl: '/images/pause.svg',
        imageHeight: 90,
        title: 'Are you still there?',
        html: message,
        showCancelButton: true,
        confirmButtonText: 'Continue',
        cancelButtonText: 'Back',
      }).then((result) => {
        if (result.isConfirmed) {
          this.switchTaskState();
        }

        this.handleTitleNotification();
      });
    },

    getDigits (value) {
      return value.toString().padStart(2, '0');
    },

    formatDate (date) {
      return this.timezone
          ? moment.utc(date).tz(this.timezone).calendar()
          : moment.utc(date).local().calendar();
    },

    getDifference (startedAt, stoppedAt) {
      const minutes = moment(stoppedAt).diff(moment(startedAt), 'minutes');

      return minutes > 59 ? (minutes / 60).toFixed(2) + ' Hours' : Math.round(minutes) + ' Minutes';
    },

    triggerDisplayDetails () {
      this.showDetails = !this.showDetails;
    },

    triggerEditMode () {
      if (this.isExternalMember) {
        this.viewDetails = !this.viewDetails;
        return;
      }

      this.editMode = !this.editMode;

      if (this.editMode) {
        this.$nextTick(() => {
          this.$refs['task-title-input'].focus();
          this.resizeTitleInputField();
        });
      }
    },

    resizeTitleInputField () {
      this.titleInputFieldHeight = 'auto';

      this.$nextTick(() => {
        this.titleInputFieldHeight = `${this.$refs['task-title-input'].scrollHeight}px`;
      });
    },

    cancelEditing (event) {
      event.preventDefault();
      this.title = this.task.title;
      this.editMode = false;

      this.$nextTick(() => {
        this.$refs['task-title-input'].blur();
      });
    },

    async saveChanges (event) {
      event.preventDefault();

      if (this.task.title !== this.title) {
        this.task.title = this.title;

        const response = await update(this.task, { title: this.task.title });

        this.$emit('taskUpdated', response.data.data);
      }

      this.triggerEditMode();
    },

    async deleteTask () {
      if (this.task.id) {
        await destroy(this.task);

        this.$emit('taskDeleted', this.task);
        this.triggerEditMode();
      }
    },

    async saveTaskLabel (value, labelId) {
      if (this.task.id) {
        const response = await update(this.task, { label_id: labelId });

        this.$emit('taskUpdated', response.data.data);
      } else {
        this.temporaryLabel = labelId;
      }
    },

    async deleteLabel (labelId) {
      this.$emit('deleteLabel', labelId);
    },

    async updateLabel (val, labelId) {
      let label = null;

      if (labelId) {
        const res = await axios.patch(`/company/${this.currentUser.company.id}/labels/${labelId}`, { label: val });
        label = res.data.data;
      } else {
        const res = await axios.post(`/company/${this.currentUser.company.id}/labels`, { label: val });
        label = res.data.data;
      }

      this.$emit('saveLabel', { val, label });
    },

    async handlePageUnload (e) {
      this.handleTaskTimer();

      Swal.fire({
        icon: 'info',
        title: 'Active task',
        html: 'You have an active task ongoing. Please stop the task before leaving the dashboard.',
        showDenyButton: true,
        denyButtonText: 'Stop & Exit',
        confirmButtonText: 'Stay',
        allowOutsideClick: false,
      }).then((result) => {
        if (result.isConfirmed) {
          this.handleTaskTimer();
        } else if (result.isDenied) {
          this.switchTaskState();
        }
      });

      e.preventDefault();
      e.returnValue = '';
    },

    registerUnloadHandler () {
      window.addEventListener('beforeunload', this.handlePageUnload, { capture: true });
    },

    deregisterUnloadHandler () {
      window.removeEventListener('beforeunload', this.handlePageUnload, { capture: true });
    },

    taskPlayed (id) {
      this.$emit('taskPlayed', id);
    },

    taskPaused () {
      this.$emit('taskPlayed', null);
    },

    registerInactivityListener () {
      this.hasInactiveListener = true;
      window.Echo.private(`playable-task.inactive.${this.task.id}`).listen(
          '.playable-task.inactive',
          () => {
            this.fetchTask(this.task.id);
            this.triggerStopTaskActions();
            this.handleOvertime();
          },
      );
      window.Echo.private(`playable-task.stopped.${this.task.id}`).listen(
          '.playable-task.stopped',
          () => {
            this.fetchTask(this.task.id);
            this.triggerStopTaskActions();
            this.handleTaskTimer();
          },
      );
    },

    deregisterInactivityListener () {
      this.hasInactiveListener = false;
      window.Echo.leave(`playable-task.inactive.${this.task.id}`);
      window.Echo.leave(`playable-task.stopped.${this.task.id}`);
    },

    async fetchTask(taskId) {
      if (!taskId) {
        return;
      }

      const response = await axios.get(route('tasks.show', this.task.id));
      this.task = response.data.data;
    },
  },

  beforeDestroy () {
    if (this.hasInactiveListener) {
      this.deregisterInactivityListener();
      this.deregisterUnloadHandler();
    }
  },

  mounted () {
    this.playable = this.task.playable ?? [];

    if (this.taskActive) {
      this.triggerStartTaskActions();
      this.handleTaskTimer();
    }
  },
};
</script>

<style scoped lang="scss">
.playable-controller {
  div:first-child {
    margin-right: 8px;
  }

  .playable-button {
    padding: 0 !important;
    border: none !important;
    outline: none !important;
    border-radius: 3px;
    width: 32px;
    height: 32px;
  }

  .playable-timer {
    color: #8997aa;
    font-family: "Open Sans", sans-serif;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0;
    line-height: 19px;
  }
}

.playable-details {
  line-height: 1.2;
  color: #8997aa;
  font-family: "Open Sans", sans-serif;
  font-size: 14px;
  letter-spacing: 0;
}

.task-title-input {
  resize: none;
  overflow: hidden;
  border-radius: 3px 0 0 3px;
  line-height: 1.3em;
  width: 190px;
}

.task-title {
  &:hover {
    cursor: pointer;
    background-color: #f0f0f0;
  }
}
</style>
